import { TenantedPropertyDetailsModelTenancyType } from '@urbanx/agx.campaigns.client'

type ComplianceItem = {
  key: string
  label: string
  requiresTenancy?: boolean
}

const REQUIRED_COMPLIANCE_ITEMS: ComplianceItem[] = [
  {
    key: 'nswCosCompliance_addressMatchesTitleSearch',
    label:
      'Confirm the property address on the Contract matches the Title Search',
  },
  {
    key: 'nswCosCompliance_vendorMatchesTitleSearch',
    label: 'Confirm the Vendor(s) name on the Contract match the Title Search',
  },
  {
    key: 'nswCosCompliance_vendorMatchesAgencyAgreement',
    label:
      'Confirm the Vendor(s) name match the Agency Agreement (If discrepancy, escalate with Admin Manager)',
  },
  {
    key: 'nswCosCompliance_addressMatchesAgencyAgreement',
    label:
      'Confirm the property address on the Contract matches the Agency Agreement (If discrepancy, escalate with Admin Manager)',
  },
  {
    key: 'nswCosCompliance_agencyDetailsCorrect',
    label: 'Confirm Agency details are correct',
  },
]

const OPTIONAL_COMPLIANCE_ITEMS: ComplianceItem[] = [
  {
    key: 'nswCosCompliance_sewerDiagramAttached',
    label: 'Confirm certificate of sewer diagram is attached',
  },
  {
    key: 'nswCosCompliance_urbanxCoAgentNoted',
    label: 'Confirm URBAN X noted as Co-Agent on the Contract',
  },
  {
    key: 'nswCosCompliance_tenancyAgreementAttached',
    label: 'Confirm Tenancy Agreement is attached',
    requiresTenancy: true,
  },
]

export type ComplianceKey =
  | (typeof REQUIRED_COMPLIANCE_ITEMS)[number]['key']
  | (typeof OPTIONAL_COMPLIANCE_ITEMS)[number]['key']

export type ComplianceData = Partial<Record<ComplianceKey, boolean>>

const ALL_COMPLIANCE_KEYS: ComplianceKey[] = [
  ...REQUIRED_COMPLIANCE_ITEMS.map((item) => item.key),
  ...OPTIONAL_COMPLIANCE_ITEMS.map((item) => item.key),
]

export const isTenancyAgreementRequired = (
  tenancyType?: TenantedPropertyDetailsModelTenancyType
) =>
  tenancyType ===
    TenantedPropertyDetailsModelTenancyType.TenantedAgencyManaged ||
  tenancyType === TenantedPropertyDetailsModelTenancyType.TenantedSelfManaged

export const getComplianceItems = (showTenancyAgreement: boolean) => ({
  required: REQUIRED_COMPLIANCE_ITEMS,
  optional: showTenancyAgreement
    ? OPTIONAL_COMPLIANCE_ITEMS
    : OPTIONAL_COMPLIANCE_ITEMS.filter((item) => !item.requiresTenancy),
})

const normalizeContractSegment = (value?: string) =>
  (value ?? '').replace(/[\\/]/g, '-').trim()

export const buildContractFileName = (street?: string, suburb?: string) => {
  const safeStreet = normalizeContractSegment(street)
  const safeSuburb = normalizeContractSegment(suburb)
  const addressSegment = safeSuburb
    ? `${safeStreet}, ${safeSuburb}`
    : safeStreet

  return `COS - ${addressSegment}.pdf`
}

export const buildComplianceState = (
  formData?: Record<string, unknown>
): ComplianceData =>
  ALL_COMPLIANCE_KEYS.reduce((acc, key) => {
    acc[key] = Object.prototype.hasOwnProperty.call(formData ?? {}, key)
      ? Boolean(formData?.[key])
      : false
    return acc
  }, {} as ComplianceData)

export const getRequiredCompletion = (complianceData: ComplianceData) =>
  REQUIRED_COMPLIANCE_ITEMS.every((item) => complianceData[item.key] === true)

const getOptionalCompletion = (
  complianceData: ComplianceData,
  showTenancyAgreement: boolean
) => {
  const optionalItems = showTenancyAgreement
    ? OPTIONAL_COMPLIANCE_ITEMS
    : OPTIONAL_COMPLIANCE_ITEMS.filter((item) => !item.requiresTenancy)

  return optionalItems.every((item) => complianceData[item.key] === true)
}

export const getFinalEnabled = (
  complianceData: ComplianceData,
  showTenancyAgreement: boolean
) =>
  getRequiredCompletion(complianceData) &&
  getOptionalCompletion(complianceData, showTenancyAgreement)
